'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';

const SYSTEM_METRICS = [
  { name: 'API Response P95', value: 245, unit: 'ms', status: 'ok', warning: 500, critical: 1000, trend: -12 },
  { name: 'DB Query Avg', value: 12, unit: 'ms', status: 'ok', warning: 50, critical: 100, trend: -2 },
  { name: 'Cache Hit Rate', value: 94.7, unit: '%', status: 'ok', warning: 80, critical: 60, trend: 1.2 },
  { name: 'Error Rate', value: 0.12, unit: '%', status: 'ok', warning: 1, critical: 5, trend: -0.03 },
  { name: 'Active Connections', value: 847, unit: '', status: 'ok', warning: 1500, critical: 2000, trend: 23 },
  { name: 'Memory Usage', value: 68.4, unit: '%', status: 'ok', warning: 80, critical: 95, trend: 2.1 },
  { name: 'CPU Usage', value: 34.2, unit: '%', status: 'ok', warning: 70, critical: 90, trend: -1.8 },
  { name: 'Pipeline Lag', value: 28, unit: 's', status: 'ok', warning: 60, critical: 120, trend: -3 },
];

const UPTIME_DATA = [
  { day: 'Lun', uptime: 99.98, errors: 12 },
  { day: 'Mar', uptime: 100, errors: 0 },
  { day: 'Mer', uptime: 99.95, errors: 23 },
  { day: 'Jeu', uptime: 100, errors: 4 },
  { day: 'Ven', uptime: 99.99, errors: 8 },
  { day: 'Sam', uptime: 100, errors: 2 },
  { day: 'Dim', uptime: 99.97, errors: 15 },
];

const AUDIT_LOGS = [
  { user: 'admin@nhl.pro', action: 'model_deployed', resource: 'random_forest v2.2.0', ip: '192.168.1.1', time: '2m ago', success: true },
  { user: 'system', action: 'pipeline_run', resource: 'nhl_official', ip: 'internal', time: '30s ago', success: true },
  { user: 'j.martin@...', action: 'api_key_created', resource: 'Production App', ip: '45.12.34.56', time: '15m ago', success: true },
  { user: 'system', action: 'backup_completed', resource: 'database_full', ip: 'internal', time: '1h ago', success: true },
  { user: 'unknown', action: 'auth_failed', resource: 'login', ip: '89.45.123.67', time: '2h ago', success: false },
  { user: 'a.tremblay@...', action: 'subscription_upgraded', resource: 'free→pro', ip: '67.89.12.34', time: '3h ago', success: true },
];

const ERROR_LOGS = [
  { type: 'API_TIMEOUT', message: 'Odds API timeout after 15s', severity: 'warning', count: 3, lastSeen: '45m ago', resolved: false },
  { type: 'RATE_LIMIT', message: 'User exceeded API rate limit', severity: 'info', count: 12, lastSeen: '1h ago', resolved: false },
  { type: 'DB_SLOW_QUERY', message: 'Query took 2.3s on historical_games', severity: 'warning', count: 1, lastSeen: '3h ago', resolved: true },
  { type: 'AUTH_FAILED', message: 'Multiple failed login attempts from 89.45.x.x', severity: 'error', count: 47, lastSeen: '2h ago', resolved: false },
];

const TOOLTIP_STYLE = { backgroundColor: '#111827', border: '1px solid #1E2D4A', borderRadius: '6px', color: '#E8EDF5', fontSize: '11px' };

const STATUS_COLOR = (status: string) => status === 'ok' ? 'text-positive' : status === 'warning' ? 'text-warning' : 'text-negative';
const STATUS_BG = (status: string) => status === 'ok' ? 'bg-positive' : status === 'warning' ? 'bg-warning' : 'bg-negative';

export default function DevOpsClient() {
  const [activeTab, setActiveTab] = useState<'overview' | 'audit' | 'errors' | 'security'>('overview');
  const [liveMetrics, setLiveMetrics] = useState(SYSTEM_METRICS);

  useEffect(() => {
    const interval = setInterval(() => {
      setLiveMetrics((prev) => prev.map((m) => ({
        ...m,
        value: Math.max(0, m.value + (Math.random() - 0.5) * m.value * 0.02),
      })));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const getStatusForMetric = (m: typeof SYSTEM_METRICS[0]) => {
    if (m.value >= m.critical) return 'critical';
    if (m.value >= m.warning) return 'warning';
    return 'ok';
  };

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">DevOps & Sécurité</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Infrastructure production · Audit logs · Monitoring · Sécurité</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded bg-positive-subtle border border-positive/20">
            <span className="w-2 h-2 rounded-full bg-positive pulse-dot" />
            <span className="text-xs font-medium text-positive font-mono-data">TOUS SYSTÈMES OPÉRATIONNELS</span>
          </div>
          <div className="flex items-center gap-2 bg-card border border-border rounded-lg p-1">
            {(['overview', 'audit', 'errors', 'security'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${activeTab === tab ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {tab === 'overview' ? 'Aperçu' : tab === 'audit' ? 'Audit' : tab === 'errors' ? 'Erreurs' : 'Sécurité'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {activeTab === 'overview' && (
        <>
          {/* System Metrics Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {liveMetrics.map((metric) => {
              const status = getStatusForMetric(metric);
              const pct = Math.min(100, (metric.value / metric.critical) * 100);
              return (
                <div key={metric.name} className="card p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-2xs text-muted-foreground">{metric.name}</span>
                    <span className={`w-2 h-2 rounded-full ${STATUS_BG(status)}`} />
                  </div>
                  <div className={`text-xl font-bold font-mono-data ${STATUS_COLOR(status)}`}>
                    {metric.value.toFixed(metric.unit === '%' ? 1 : 0)}{metric.unit}
                  </div>
                  <div className="mt-2 h-1.5 bg-card-hover rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${status === 'ok' ? 'bg-positive' : status === 'warning' ? 'bg-warning' : 'bg-negative'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className={`text-2xs mt-1 font-mono-data ${metric.trend > 0 ? 'text-warning' : 'text-positive'}`}>
                    {metric.trend > 0 ? '+' : ''}{metric.trend}{metric.unit}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Uptime Chart */}
          <div className="card p-4">
            <h3 className="text-sm font-semibold text-foreground mb-4">Uptime 7 Jours — 99.98% SLA</h3>
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={UPTIME_DATA}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E2D4A" />
                <XAxis dataKey="day" tick={{ fill: '#6B7A99', fontSize: 10 }} />
                <YAxis domain={[99.9, 100.1]} tick={{ fill: '#6B7A99', fontSize: 10 }} />
                <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v: number) => [`${v}%`, 'Uptime']} />
                <Area type="monotone" dataKey="uptime" stroke="#00C853" fill="#00C853" fillOpacity={0.1} strokeWidth={2} name="Uptime %" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Backup Status */}
          <div className="card p-4">
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="CloudArrowUpIcon" size={16} className="text-primary" />
              Backups Automatiques
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { type: 'Base de données', last: 'Il y a 1h', next: 'Dans 5h', size: '2.4 GB', status: 'ok' },
                { type: 'Modèles ML', last: 'Il y a 2h', next: 'Dans 22h', size: '1.8 GB', status: 'ok' },
                { type: 'Logs système', last: 'Il y a 30m', next: 'Dans 30m', size: '456 MB', status: 'ok' },
              ].map((backup) => (
                <div key={backup.type} className="p-3 rounded-lg bg-card-hover border border-border/50">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2 h-2 rounded-full bg-positive" />
                    <span className="text-xs font-semibold text-foreground">{backup.type}</span>
                  </div>
                  <div className="space-y-1 text-2xs">
                    <div className="flex justify-between"><span className="text-muted-foreground">Dernier</span><span className="text-foreground">{backup.last}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Prochain</span><span className="text-primary">{backup.next}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Taille</span><span className="font-mono-data text-foreground">{backup.size}</span></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {activeTab === 'audit' && (
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-foreground mb-4">Audit Logs — Actions Système</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  {['Utilisateur', 'Action', 'Ressource', 'IP', 'Temps', 'Statut'].map((h) => (
                    <th key={h} className="text-left py-2 px-3 text-muted-foreground font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {AUDIT_LOGS.map((log, i) => (
                  <tr key={i} className="border-b border-border/50 hover:bg-card-hover transition-colors">
                    <td className="py-2 px-3 text-foreground">{log.user}</td>
                    <td className="py-2 px-3 font-mono-data text-primary">{log.action}</td>
                    <td className="py-2 px-3 text-muted-foreground">{log.resource}</td>
                    <td className="py-2 px-3 font-mono-data text-muted-foreground">{log.ip}</td>
                    <td className="py-2 px-3 text-muted-foreground">{log.time}</td>
                    <td className="py-2 px-3">
                      <span className={`status-badge ${log.success ? 'bg-positive-subtle text-positive' : 'bg-negative/10 text-negative'}`}>
                        {log.success ? '✓ OK' : '✗ FAIL'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'errors' && (
        <div className="space-y-3">
          {ERROR_LOGS.map((err, i) => (
            <div key={i} className={`card p-4 border ${err.severity === 'error' ? 'border-negative/30' : err.severity === 'warning' ? 'border-warning/30' : 'border-border'}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`status-badge text-2xs ${err.severity === 'error' ? 'bg-negative/10 text-negative' : err.severity === 'warning' ? 'bg-warning/10 text-warning' : 'bg-info-subtle text-primary'}`}>
                    {err.severity.toUpperCase()}
                  </span>
                  <span className="text-sm font-semibold text-foreground font-mono-data">{err.type}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xs text-muted-foreground">{err.lastSeen}</span>
                  {err.resolved && <span className="status-badge bg-positive-subtle text-positive text-2xs">Résolu</span>}
                </div>
              </div>
              <p className="text-xs text-muted-foreground mb-2">{err.message}</p>
              <div className="text-2xs text-muted-foreground">Occurrences: <span className="font-mono-data text-foreground">{err.count}</span></div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'security' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="card p-4">
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="ShieldCheckIcon" size={16} className="text-positive" />
              Protection Active
            </h3>
            <div className="space-y-3">
              {[
                { feature: 'Rate Limiting', status: 'Actif', detail: '60 req/min par IP', ok: true },
                { feature: 'DDoS Protection', status: 'Actif', detail: 'Cloudflare WAF', ok: true },
                { feature: 'API Key Encryption', status: 'Actif', detail: 'SHA-256 hash', ok: true },
                { feature: 'SSL/TLS', status: 'Actif', detail: 'TLS 1.3', ok: true },
                { feature: 'SQL Injection Protection', status: 'Actif', detail: 'Parameterized queries', ok: true },
                { feature: 'CORS Policy', status: 'Configuré', detail: 'Whitelist domains', ok: true },
                { feature: 'Auth Brute Force', status: 'Actif', detail: '5 tentatives max', ok: true },
                { feature: 'Data Encryption at Rest', status: 'Actif', detail: 'AES-256', ok: true },
              ].map((item) => (
                <div key={item.feature} className="flex items-center justify-between p-2 rounded bg-card-hover">
                  <div className="flex items-center gap-2">
                    <Icon name="CheckCircleIcon" size={14} className="text-positive" />
                    <span className="text-xs text-foreground">{item.feature}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-2xs text-positive font-semibold">{item.status}</div>
                    <div className="text-2xs text-muted-foreground">{item.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="card p-4">
            <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="ExclamationTriangleIcon" size={16} className="text-warning" />
              Alertes Sécurité Récentes
            </h3>
            <div className="space-y-3">
              {[
                { type: 'Brute Force', detail: '47 tentatives depuis 89.45.123.67', severity: 'high', time: '2h ago' },
                { type: 'Rate Limit', detail: 'API key nhl_pk_live_xxx dépassée', severity: 'medium', time: '4h ago' },
                { type: 'Scan Détecté', detail: 'Scan de ports depuis 45.12.x.x', severity: 'low', time: '6h ago' },
                { type: 'Auth Suspecte', detail: 'Connexion depuis nouveau pays: RU', severity: 'medium', time: '8h ago' },
              ].map((alert, i) => (
                <div key={i} className={`p-3 rounded-lg border ${alert.severity === 'high' ? 'border-negative/30 bg-negative/5' : alert.severity === 'medium' ? 'border-warning/30 bg-warning/5' : 'border-border bg-card-hover'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-foreground">{alert.type}</span>
                    <span className="text-2xs text-muted-foreground">{alert.time}</span>
                  </div>
                  <p className="text-2xs text-muted-foreground">{alert.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
